const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/powtoon-s2s`;

import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";

const logger = createLogger("powtoon-s2s.client");

async function call<T>(action: string, params: Record<string, unknown>, op?: string): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const res = await fetch(appendOp(ENDPOINT, op ?? action.replace(/_/g, "-")), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({ action, ...params }),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `powtoon-s2s ${action} failed (${res.status})`;
    logger.error("powtoon-s2s call failed", {
      action, status: res.status, correlationId, durationMs, error: msg,
    });
    throw new Error(msg);
  }
  logger.info("powtoon-s2s call ok", { action, status: res.status, correlationId, durationMs });
  return data as T;
}

export const powtoonS2S = {
  trackPublicSession: (input: {
    scenarioId: string;
    publicLinkToken: string;
    content: Record<string, unknown>;
  }) =>
    call<{ id?: string }>("track_session", {
      scenario_id: input.scenarioId,
      public_link_token: input.publicLinkToken,
      content: input.content,
    }),

  trackPublicTranscript: (input: {
    scenarioId: string;
    publicLinkToken: string;
    content: Record<string, unknown>;
  }) =>
    call<{ id?: string }>("track_transcript", {
      scenario_id: input.scenarioId,
      public_link_token: input.publicLinkToken,
      content: input.content,
    }),

  startPublicScoringJob: (input: {
    scenarioId?: string;
    transcriptObjectId: string;
    publicLinkToken: string;
    provider?: string;
    questionnaireIds?: string[];
    interactionMode?: string;
  }) =>
    call<{ id?: string; status?: string }>("start_scoring_job", {
      ...(input.scenarioId ? { scenario_id: input.scenarioId } : {}),
      transcript_object_id: input.transcriptObjectId,
      public_link_token: input.publicLinkToken,
      ...(input.provider ? { provider: input.provider } : {}),
      ...(input.interactionMode ? { interaction_mode: input.interactionMode } : {}),
      ...(input.questionnaireIds?.length
        ? {
            questionnaire_ids: input.questionnaireIds,
            questionnaire_object_id: input.questionnaireIds[0],
          }
        : {}),
    }),
};

/**
 * Fire-and-forget POST that survives page unload (uses `fetch` with
 * `keepalive`). Used to record session/transcript when the host iframe
 * is removed by the parent and we can't await responses.
 */
export function powtoonS2SBeacon(action: string, params: Record<string, unknown>): void {
  try {
    const body = JSON.stringify({ action, ...params });
    fetch(appendOp(ENDPOINT, `${action.replace(/_/g, "-")}-beacon`), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        apikey: SUPABASE_KEY,
        authorization: `Bearer ${SUPABASE_KEY}`,
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}
